
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";

interface ReportingPeriodData {
  month: string;
  revenue: number;
  expenses: number;
  newStudents: number;
  withdrawals: number;
  ballet: number;
  jazz: number;
  tap: number;
  contemporary: number;
}

export function useReportingData(dateRange?: { from: Date; to: Date }) {
  const { user } = useAuth();
  const organizationId = user?.user_metadata?.organization_id;
  
  const { data: enrollmentData } = useQuery({
    queryKey: ['enrollments', organizationId, dateRange],
    queryFn: async () => {
      const { data: enrollments, error } = await supabase
        .from('enrollments')
        .select('created_at, status')
        .eq('organization_id', organizationId);
        
      if (error) throw error;
      
      // Process enrollments into monthly stats
      const monthlyStats = enrollments.reduce((acc: Record<string, { new: number; withdrawn: number }>, enrollment) => {
        const month = new Date(enrollment.created_at).toLocaleString('default', { month: 'short' });
        if (!acc[month]) {
          acc[month] = { new: 0, withdrawn: 0 };
        }
        if (enrollment.status === 'active') {
          acc[month].new++;
        } else if (enrollment.status === 'withdrawn') {
          acc[month].withdrawn++;
        }
        return acc;
      }, {});
      
      return Object.entries(monthlyStats).map(([month, stats]) => ({
        month,
        newStudents: stats.new,
        withdrawals: stats.withdrawn
      }));
    },
    enabled: !!organizationId
  });

  const { data: revenueData } = useQuery({
    queryKey: ['revenue', organizationId, dateRange],
    queryFn: async () => {
      const { data: payments, error } = await supabase
        .from('payments')
        .select('amount_cents, paid_at')
        .eq('organization_id', organizationId)
        .order('paid_at');
        
      if (error) throw error;
      
      // Process payments into monthly revenue
      const monthlyRevenue = payments.reduce((acc: Record<string, { revenue: number }>, payment) => {
        const month = new Date(payment.paid_at).toLocaleString('default', { month: 'short' });
        if (!acc[month]) {
          acc[month] = { revenue: 0 };
        }
        acc[month].revenue += payment.amount_cents / 100; // Convert cents to dollars
        return acc;
      }, {});
      
      return Object.entries(monthlyRevenue).map(([month, data]) => ({
        month,
        revenue: data.revenue,
        expenses: data.revenue * 0.6 // Simplified expense calculation
      }));
    },
    enabled: !!organizationId
  });

  const { data: attendanceData } = useQuery({
    queryKey: ['attendance', organizationId, dateRange],
    queryFn: async () => {
      const { data: attendance, error } = await supabase
        .from('class_attendance')
        .select('status, date')
        .eq('organization_id', organizationId);
        
      if (error) throw error;
      
      // Calculate monthly attendance percentages
      const monthlyAttendance = attendance.reduce((acc: Record<string, { total: number; present: number }>, record) => {
        const month = new Date(record.date).toLocaleString('default', { month: 'short' });
        if (!acc[month]) {
          acc[month] = { total: 0, present: 0 };
        }
        acc[month].total++;
        if (record.status === 'present') {
          acc[month].present++;
        }
        return acc;
      }, {});
      
      return Object.entries(monthlyAttendance).map(([month, stats]) => ({
        month,
        attendance: Math.round((stats.present / stats.total) * 100)
      }));
    },
    enabled: !!organizationId
  });

  return {
    enrollmentData: enrollmentData || [],
    revenueData: revenueData || [],
    attendanceData: attendanceData || []
  };
}
